import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'convex/react'
import { api } from '../../convex/_generated/api'
import { Id } from '../../convex/_generated/dataModel'
import { Header } from '../components/layout/Header'
import { PhoneNumber } from '../components/dashboard/PhoneNumber'
import { UsageMetrics } from '../components/dashboard/UsageMetrics'
import { FeatureCard } from '../components/dashboard/FeatureCard'

/**
 * LovedOneDetail - Main dashboard view for a single loved one
 * Displays profile, phone number, usage metrics, features, and recent activity
 *
 * Desktop: Phone+Metrics side by side, Features+Activity side by side
 * Mobile: Single column stack
 */
export function LovedOneDetail() {
  const { t } = useTranslation()
  const { lovedOneId } = useParams<{ lovedOneId: string }>()

  const detail = useQuery(
    api.dashboard.getLovedOneDetail,
    lovedOneId ? { lovedOneId: lovedOneId as Id<'lovedOnes'> } : 'skip'
  )

  const formatDate = (timestamp: number): string => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(timestamp))
  }

  const formatDuration = (seconds?: number): string => {
    if (!seconds) return ''
    const min = Math.floor(seconds / 60)
    const sec = seconds % 60
    if (min === 0) return `${sec} sec`
    if (sec === 0) return `${min} min`
    return `${min} min ${sec} sec`
  }

  if (!lovedOneId) {
    return (
      <div className="dashboard-container">
        <Header />
        <div className="dashboard-content">
          <p>Invalid loved one ID</p>
        </div>
        <style>{styles}</style>
      </div>
    )
  }

  if (detail === undefined) {
    return (
      <div className="dashboard-container">
        <Header />
        <div className="dashboard-content">
          <p className="loading-text">{t('common.loading')}</p>
        </div>
        <style>{styles}</style>
      </div>
    )
  }

  const { lovedOne, metrics, features, recentActivity } = detail

  const getMedicationSummary = () => {
    const count = features.medicationReminders.medicationCount
    if (count === 0) return undefined
    const label = count === 1 ? t('dashboard.medication') : t('dashboard.medications')
    return `${count} ${label}`
  }

  return (
    <div className="dashboard-container">
      <Header />
      <div className="dashboard-content">
        {/* Back link */}
        <Link to="/dashboard" className="back-link">
          ← {t('dashboard.yourLovedOnes')}
        </Link>

        {/* Header Grid - aligns with main grid columns */}
        <div className="header-grid">
          {/* Left column: Profile */}
          <div className="header-left">
            <div className="profile-section">
              <div className="avatar">
                {lovedOne.profilePhotoUrl ? (
                  <img src={lovedOne.profilePhotoUrl} alt={lovedOne.name} />
                ) : (
                  lovedOne.name.charAt(0).toUpperCase()
                )}
              </div>
              <h1 className="loved-one-name">{lovedOne.name}</h1>
            </div>
          </div>

          {/* Right column: Phone Number (aligned with Recent Activity) */}
          <div className="header-right">
            <PhoneNumber />
          </div>
        </div>

        {/* Usage Metrics - below header, left aligned */}
        <div className="metrics-row">
          <UsageMetrics callsThisWeek={metrics.callsThisWeek} lovedOneId={lovedOneId} />
        </div>

        {/* Main Content: Features + Activity in responsive grid */}
        <div className="main-grid">
          {/* Features Column */}
          <div className="features-column">
            <h2 className="section-title">{t('dashboard.features')}</h2>
            <div className="feature-grid">
              <FeatureCard
                type="medication_reminders"
                status={features.medicationReminders.status}
                summary={getMedicationSummary()}
                lovedOneId={lovedOneId}
                isPaused={features.medicationReminders.isPaused}
              />
              <FeatureCard
                type="shopping_assistance"
                status={features.shoppingAssistance.status}
                lovedOneId={lovedOneId}
              />
            </div>
          </div>

          {/* Recent Activity Column */}
          <div className="activity-column">
            <h2 className="section-title">{t('dashboard.recentActivity')}</h2>
            <div className="activity-card">
              {recentActivity.length === 0 ? (
                <p className="no-activity">{t('activity.noActivity')}</p>
              ) : (
                recentActivity.map((activity, index) => (
                  <div
                    key={activity._id}
                    className={`activity-item ${index === recentActivity.length - 1 ? 'last' : ''}`}
                  >
                    <div className="activity-header">
                      <span className="activity-direction">
                        {activity.direction === 'incoming' ? '📥' : '📤'}{' '}
                        {activity.direction === 'incoming'
                          ? t('activity.incoming')
                          : t('activity.outgoing')}
                        {activity.duration ? ` • ${formatDuration(activity.duration)}` : ''}
                      </span>
                      <span className="activity-time">{formatDate(activity.startedAt)}</span>
                    </div>
                    {activity.summary && <p className="activity-summary">{activity.summary}</p>}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .dashboard-container {
    min-height: 100vh;
    background-color: #f8f9fa;
    font-family: system-ui, -apple-system, sans-serif;
  }

  .dashboard-content {
    padding: 1.5rem 1rem;
    max-width: 1200px;
    margin: 0 auto;
  }

  .back-link {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    color: #0066cc;
    text-decoration: none;
    font-size: 14px;
    margin-bottom: 1rem;
    min-height: 44px;
  }

  .loading-text {
    text-align: center;
    padding: 2rem;
    color: #666;
  }

  /* Header Grid - matches main grid columns for alignment */
  .header-grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: 1fr;
    margin-bottom: 1rem;
  }

  @media (min-width: 768px) {
    .header-grid {
      grid-template-columns: 1fr 1fr;
      align-items: center;
    }
  }

  @media (min-width: 1024px) {
    .header-grid {
      grid-template-columns: minmax(300px, 400px) 1fr;
    }
  }

  .header-left {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .header-right {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .profile-section {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .avatar {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background-color: #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    color: #6b7280;
    overflow: hidden;
    flex-shrink: 0;
  }

  .avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .loved-one-name {
    font-size: clamp(20px, 4vw, 28px);
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
  }

  /* Metrics Row - below header */
  .metrics-row {
    margin-bottom: 0.5rem;
  }

  @media (min-width: 768px) {
    .metrics-row {
      max-width: 400px;
    }
  }

  /* Main Grid - Desktop: 2 columns, Mobile: 1 column */
  .main-grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: 1fr;
  }

  @media (min-width: 768px) {
    .main-grid {
      grid-template-columns: 1fr 1fr;
      align-items: start;
    }
  }

  @media (min-width: 1024px) {
    .main-grid {
      grid-template-columns: minmax(300px, 400px) 1fr;
    }
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #374151;
    margin: 0 0 0.75rem 0;
  }

  /* Features */
  .features-column {
    margin-top: 1rem;
  }

  .feature-grid {
    display: grid;
    gap: 0.75rem;
    grid-template-columns: 1fr;
  }

  @media (min-width: 480px) and (max-width: 767px) {
    .feature-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  /* Activity */
  .activity-column {
    margin-top: 1rem;
  }

  .activity-card {
    background-color: #fff;
    border-radius: 12px;
    padding: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .activity-item {
    padding: 0.75rem 0;
    border-bottom: 1px solid #f3f4f6;
  }

  .activity-item.last {
    border-bottom: none;
  }

  .activity-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.25rem;
  }

  .activity-direction {
    font-size: 14px;
    font-weight: 500;
    color: #374151;
  }

  .activity-time {
    font-size: 12px;
    color: #9ca3af;
  }

  .activity-summary {
    font-size: 14px;
    color: #6b7280;
    line-height: 1.4;
    margin: 0.25rem 0 0 0;
  }

  .no-activity {
    color: #9ca3af;
    font-size: 14px;
    text-align: center;
    padding: 1rem;
    margin: 0;
  }
`
